import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface ExecutiveMetric {
  title: string;
  value: string;
  change: string;
  trend: 'positive' | 'negative' | 'neutral';
}

interface InsightCard {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  category: string;
  actionable: boolean;
}

interface FAQ {
  question: string;
  answer: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
}

@Component({
  selector: 'app-executive-summary',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
              <span>Premium Subscription</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Executive Summary
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Get concise, actionable insights to help you make critical business decisions faster with our comprehensive industry intelligence reports.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#contact" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Request a Demo
              </a>
              <a href="#features" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Explore Features
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1000" 
                   alt="Executive Summary Dashboard showing business metrics and KPIs" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Benefits Section -->
    <section class="py-16 bg-gray-50" id="features">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Key Benefits</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Executive Summary provides business leaders with the critical information they need to make informed decisions quickly across packaging, forest products, and related industries.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (feature of features; track feature.title) {
            <div class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
              <div class="text-blue-600 text-4xl mb-4">
                <i class="fas {{ feature.icon }}"></i>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ feature.title }}</h3>
              <p class="text-gray-600">{{ feature.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">How It Works</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Executive Summary delivers actionable intelligence through a simple, streamlined process.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div class="text-center">
            <div class="bg-blue-100 text-blue-600 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
            <h3 class="text-xl font-bold mb-2">Data Collection</h3>
            <p class="text-gray-600">We gather data from thousands of sources across your industry</p>
          </div>
          <div class="text-center">
            <div class="bg-blue-100 text-blue-600 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
            <h3 class="text-xl font-bold mb-2">AI Analysis</h3>
            <p class="text-gray-600">Our AI analyzes and identifies key trends and insights</p>
          </div>
          <div class="text-center">
            <div class="bg-blue-100 text-blue-600 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
            <h3 class="text-xl font-bold mb-2">Expert Review</h3>
            <p class="text-gray-600">Industry experts review and enhance the findings</p>
          </div>
          <div class="text-center">
            <div class="bg-blue-100 text-blue-600 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
            <h3 class="text-xl font-bold mb-2">Delivery</h3>
            <p class="text-gray-600">Concise reports delivered to your dashboard and inbox</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from business leaders who rely on our Executive Summary for critical decision-making.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (testimonial of testimonials; track testimonial.author) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div class="text-blue-600 text-4xl mb-4">
                <i class="fas fa-quote-left"></i>
              </div>
              <p class="text-gray-700 mb-6 italic">{{ testimonial.quote }}</p>
              <div class="flex items-center">
                <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold mr-4">
                  {{ testimonial.author.charAt(0) }}
                </div>
                <div>
                  <p class="font-bold text-gray-900">{{ testimonial.author }}</p>
                  <p class="text-gray-600 text-sm">{{ testimonial.position }}, {{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-white" id="faq">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our Executive Summary service.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (faq of faqs; track faq.question) {
            <div class="mb-6 border-b border-gray-200 pb-6">
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ faq.question }}</h3>
              <p class="text-gray-600">{{ faq.answer }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-blue-900 to-indigo-900 text-white" id="contact">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Decision-Making?</h2>
          <p class="text-xl mb-8">
            Join industry leaders who rely on our Executive Summary for critical business intelligence.
          </p>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <h3 class="text-2xl font-bold mb-6">Request a Demo</h3>
            <form class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium mb-2">First Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Last Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company Email</label>
                <input type="email" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-2">How can we help you?</label>
                <textarea rows="4" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400"></textarea>
              </div>
              <div class="md:col-span-2">
                <button type="submit" class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .executive-summary {
      padding: 3rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .summary-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--primary-color);
      margin-bottom: 0.5rem;
    }

    .subtitle {
      color: var(--text-secondary);
      font-size: 1.1rem;
    }

    .header-actions {
      display: flex;
      gap: 1rem;
    }

    .action-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.5rem;
      border-radius: 4px;
      font-weight: 500;
      cursor: pointer;
      border: 1px solid var(--border-color);
      background: white;
      color: var(--text-primary);
      transition: all 0.2s;
    }

    .action-button:hover {
      background: var(--background-light);
      transform: translateY(-2px);
    }

    .content {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .metrics-overview {
      margin-bottom: 3rem;
    }

    h2 {
      font-size: 1.75rem;
      color: var(--text-primary);
      margin-bottom: 1.5rem;
    }

    .metrics-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 1.5rem;
    }

    .metric-card {
      padding: 1.5rem;
      background: var(--background-light);
      border-radius: 8px;
      transition: transform 0.2s;
    }

    .metric-card:hover {
      transform: translateY(-4px);
    }

    .metric-card h3 {
      font-size: 1rem;
      color: var(--text-secondary);
      margin-bottom: 0.5rem;
    }

    .metric-value {
      font-size: 2rem;
      font-weight: bold;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .metric-change {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.9rem;
    }

    .metric-change.positive { color: var(--success-color); }
    .metric-change.negative { color: var(--danger-color); }
    .metric-change.neutral { color: var(--warning-color); }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }

    .filters {
      display: flex;
      gap: 0.5rem;
    }

    .filter-button {
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      border: 1px solid var(--border-color);
      background: white;
      color: var(--text-secondary);
      cursor: pointer;
      transition: all 0.2s;
    }

    .filter-button.active {
      background: var(--primary-color);
      color: white;
      border-color: var(--primary-color);
    }

    .insights-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1.5rem;
    }

    .insight-card {
      padding: 1.5rem;
      background: white;
      border-radius: 8px;
      border: 1px solid var(--border-color);
      transition: transform 0.2s;
    }

    .insight-card:hover {
      transform: translateY(-4px);
    }

    .insight-card.high { border-left: 4px solid var(--danger-color); }
    .insight-card.medium { border-left: 4px solid var(--warning-color); }
    .insight-card.low { border-left: 4px solid var(--success-color); }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    .category-badge {
      font-size: 0.8rem;
      color: var(--text-secondary);
      font-weight: 500;
    }

    .actionable-badge {
      padding: 0.25rem 0.75rem;
      background: var(--success-color-light);
      color: var(--success-color);
      border-radius: 1rem;
      font-size: 0.8rem;
      font-weight: 500;
    }

    .insight-card h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .insight-card p {
      color: var(--text-secondary);
      line-height: 1.6;
      margin-bottom: 1rem;
    }

    .card-actions {
      display: flex;
      gap: 1rem;
    }

    .action-link {
      color: var(--primary-color);
      background: none;
      border: none;
      padding: 0;
      font-weight: 500;
      cursor: pointer;
      transition: color 0.2s;
    }

    .action-link:hover {
      color: var(--primary-color-dark);
    }

    @media (max-width: 768px) {
      .summary-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
      }

      .header-actions {
        width: 100%;
      }

      .action-button {
        flex: 1;
        justify-content: center;
      }

      .section-header {
        flex-direction: column;
        gap: 1rem;
      }

      .filters {
        width: 100%;
        overflow-x: auto;
        padding-bottom: 0.5rem;
      }

      .filter-button {
        white-space: nowrap;
      }
    }

    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class ExecutiveSummaryComponent {
  executiveMetrics: ExecutiveMetric[] = [
    {
      title: 'Revenue Growth',
      value: '$12.4M',
      change: '+15.2%',
      trend: 'positive'
    },
    {
      title: 'Market Share',
      value: '23.5%',
      change: '+2.1%',
      trend: 'positive'
    },
    {
      title: 'Customer Satisfaction',
      value: '4.8/5.0',
      change: '0%',
      trend: 'neutral'
    },
    {
      title: 'Operational Costs',
      value: '$5.2M',
      change: '-3.5%',
      trend: 'negative'
    }
  ];

  strategicInsights: InsightCard[] = [
    {
      title: 'Market Expansion Opportunity',
      description: 'New market analysis suggests potential for 30% growth in APAC region.',
      priority: 'high',
      category: 'Market',
      actionable: true
    },
    {
      title: 'Supply Chain Optimization',
      description: 'Identified potential cost savings of 15% through supplier consolidation.',
      priority: 'medium',
      category: 'Operations',
      actionable: true
    },
    {
      title: 'Competitive Analysis',
      description: 'Key competitor launching new product line in Q3 2024.',
      priority: 'high',
      category: 'Market',
      actionable: false
    }
  ];

  features: Feature[] = [
    {
      icon: 'fa-chart-line',
      title: 'Strategic Insights',
      description: 'Gain high-level strategic insights into market trends, competitive landscapes, and industry developments to inform your business strategy and decision-making process.'
    },
    {
      icon: 'fa-bolt',
      title: 'Time Efficiency',
      description: 'Save valuable time with concise, actionable summaries of complex industry data, allowing executives to quickly grasp essential information without sifting through extensive reports.'
    },
    {
      icon: 'fa-bullseye',
      title: 'Focused Analysis',
      description: 'Receive targeted analysis of the most critical factors affecting your industry, with expert commentary on implications and potential strategic responses.'
    },
    {
      icon: 'fa-chart-pie',
      title: 'Data Visualization',
      description: 'Complex data presented through intuitive visualizations and dashboards, making it easier to identify patterns, trends, and outliers at a glance.'
    },
    {
      icon: 'fa-lightbulb',
      title: 'Actionable Recommendations',
      description: 'Get specific, actionable recommendations based on industry intelligence, helping you translate insights into concrete business initiatives and strategic decisions.'
    },
    {
      icon: 'fa-sync',
      title: 'Regular Updates',
      description: 'Stay informed with regular updates on key metrics and developments, ensuring you always have the most current information to guide your decision-making.'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "The Executive Summary has become an essential tool for our leadership team. It provides us with the critical insights we need to make informed decisions quickly.",
      author: "Sarah Johnson",
      position: "Chief Strategy Officer",
      company: "Global Manufacturing Inc."
    },
    {
      quote: "What impresses me most is how the Executive Summary cuts through the noise to deliver exactly what we need to know. It's saved us countless hours of research.",
      author: "Michael Chen",
      position: "VP of Operations",
      company: "Pacific Innovations"
    },
    {
      quote: "We've been able to identify new market opportunities months before our competitors thanks to the insights from the Executive Summary reports.",
      author: "Jessica Rodriguez",
      position: "Director of Business Development",
      company: "Horizon Enterprises"
    }
  ];

  faqs: FAQ[] = [
    {
      question: "How frequently are Executive Summary reports delivered?",
      answer: "Our Executive Summary reports are delivered on a weekly basis, with special alerts for critical developments that require immediate attention."
    },
    {
      question: "Can the Executive Summary be customized for our specific industry?",
      answer: "Yes, we tailor the Executive Summary to focus on your specific industry, competitors, and areas of interest to ensure you receive the most relevant insights."
    },
    {
      question: "How is the information in the Executive Summary gathered and verified?",
      answer: "We collect data from thousands of trusted sources, which is then analyzed by our AI systems and verified by industry experts to ensure accuracy and relevance."
    },
    {
      question: "Can multiple team members access the Executive Summary?",
      answer: "Yes, we offer flexible subscription options that allow multiple team members to access the Executive Summary through our online dashboard."
    },
    {
      question: "Is historical data available in the Executive Summary?",
      answer: "Yes, subscribers have access to an archive of past reports, allowing you to track trends and developments over time."
    }
  ];
} 